import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'wouter';
import {
  Bookmark,
  Calendar,
  ChartColumn,
  ChevronRight,
  Compass,
  Eye,
  FileText,
  GraduationCap,
  Handshake,
  Headphones,
  IdCard,
  LayoutDashboard,
  Megaphone,
  MessageCircle,
  Plane,
  Receipt,
  Sparkles,
  SquareCheck,
  X,
  Zap,
} from 'lucide-react';

import { cn } from '@/lib/utils';
import { BELOW_CHROME_HEIGHT } from '@/components/GlobalHeader';
import { useResolvedFeatureFlags } from '@/hooks/useResolvedFeatureFlags';
import { FINANCES_NAV_MATCH_PATHS, FINANCES_PATHS } from '@/lib/financesPaths';
import { usePhase1Bookmarks } from '@/lib/phase1Bookmarks';
import {
  PHASE1_GROUP_LABELS,
  PHASE1_PAGES,
  phase1ModuleFor,
  phase1Path,
  phase1VisibleModules,
  type Phase1GroupId,
  type Phase1Module,
} from '@/lib/phase1Paths';
import { UserMenu } from './UserMenu';

const ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  Bookmark,
  Calendar,
  ChartColumn,
  Compass,
  Eye,
  FileText,
  GraduationCap,
  Handshake,
  Headphones,
  IdCard,
  LayoutDashboard,
  Megaphone,
  MessageCircle,
  Plane,
  Receipt,
  Sparkles,
  SquareCheck,
  Zap,
};

const GROUP_ORDER: Phase1GroupId[] = ['loop', 'workspace', 'network', 'me'];

/**
 * The rows this member sees: the phase's rows, with Finances kept behind the
 * flag it already ships behind (`invoicingMvp`) and given its real address.
 */
export function usePhase1Rows(): Phase1Module[] {
  const { isEnabled, isLoading } = useResolvedFeatureFlags();
  const financesOn = !isLoading && isEnabled('invoicingMvp');
  return useMemo(
    () =>
      phase1VisibleModules()
        .filter((m) => m.id !== 'finances' || financesOn)
        .map((m) =>
          m.id === 'finances'
            ? { ...m, path: FINANCES_PATHS.invoices, matchPaths: [...FINANCES_NAV_MATCH_PATHS] }
            : m,
        ),
    [financesOn],
  );
}

export const phase1RowActive = (m: Phase1Module, location: string): boolean => {
  if (m.id === 'dashboard') return location === '/';
  const owner = phase1ModuleFor(location);
  if (owner && m.id === owner.id) return true;
  return (m.matchPaths ?? []).some((p) => location === p || location.startsWith(`${p}/`));
};

export const phase1Icon = (m: Phase1Module) => ICONS[m.icon] ?? Compass;

interface Phase1RailProps {
  inDrawer?: boolean;
  onNavigated?: () => void;
  showUserMenu?: boolean;
  /** Passed through to the user menu — Threads uses both (TraditionalSidebar). */
  onMenuStateChange?: (open: boolean) => void;
  onImportLegacyChats?: () => void;
}

/**
 * AR-445 — the Level-1 rail of the Phase-1 navigation (D-NAV-01, Concept A).
 *
 * Four groups: the management loop (unlabelled; Dashboard only in Phase 1),
 * Workspace, Network and WorldVia Me. Each row is a module; a module with a
 * Level-2 column gets it from the layout beside the rail. The Bookmarks row is
 * not a page — it opens the Bookmarks panel beside the rail, the place the
 * Level-2 column takes (prototype `favRow()`).
 *
 * Rendered by UnifiedSidebar whenever the Phase-1 flags are on, so every layout
 * that already shows the rail — Threads, Finances, Account, Settings — gets the
 * new navigation with no change of its own.
 */
export const Phase1Rail: React.FC<Phase1RailProps> = ({
  inDrawer = false,
  onNavigated,
  showUserMenu = true,
  onMenuStateChange,
  onImportLegacyChats,
}) => {
  const [location, navigate] = useLocation();
  const rows = usePhase1Rows();
  // Below 1024px the rail starts as icons, so a tablet keeps room for the
  // Level-2 column and the page beside it.
  const [expanded, setExpanded] = useState(
    () => typeof window === 'undefined' || window.matchMedia('(min-width: 1024px)').matches,
  );
  const [panelOpen, setPanelOpen] = useState(false);
  const bookmarksRowRef = useRef<HTMLButtonElement>(null);

  // A navigation closes the panel, the way choosing a row in a column would.
  useEffect(() => setPanelOpen(false), [location]);

  const groups = GROUP_ORDER.map((g) => ({
    id: g,
    label: PHASE1_GROUP_LABELS[g],
    rows: rows.filter((r) => r.group === g),
  })).filter((g) => g.rows.length);

  const open = (m: Phase1Module) => {
    if (m.id === 'bookmarks') {
      setPanelOpen((v) => !v);
      return;
    }
    if (m.path) navigate(m.path);
    onNavigated?.();
  };

  const collapsed = !expanded && !inDrawer;

  return (
    <div
      data-app-chrome
      data-phase1-rail
      className={cn(
        'relative flex flex-col bg-gray-50/50 dark:bg-gray-900/50',
        inDrawer
          ? 'w-full'
          : cn('border-r border-gray-200 dark:border-gray-700', collapsed ? 'w-12' : 'w-56'),
      )}
      style={{ height: inDrawer ? undefined : BELOW_CHROME_HEIGHT }}
    >
      {!inDrawer && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="absolute right-[-11px] top-1/2 z-[200] flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-full border border-gray-300 bg-white shadow-sm hover:border-gray-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-color)] dark:border-gray-600 dark:bg-gray-800"
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-expanded={!collapsed}
        >
          <ChevronRight
            className={cn('h-3 w-3 text-gray-500 dark:text-gray-300', !collapsed && 'rotate-180')}
          />
        </button>
      )}

      <nav
        aria-label="Main"
        className={cn(
          'pb-2',
          collapsed ? 'px-2 pt-3' : 'px-3 pt-3',
          !inDrawer && 'flex-1 overflow-y-auto overflow-x-hidden',
        )}
      >
        {groups.map((g) => (
          <div key={g.id} className={cn(g.label && 'mt-3')}>
            {g.label ? (
              collapsed ? (
                <div aria-hidden className="mx-1 mb-2 border-t border-gray-200 dark:border-gray-700" />
              ) : (
                <h2 className="mb-1 px-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-500 dark:text-gray-400">
                  {g.label}
                </h2>
              )
            ) : null}
            <ul className="space-y-0.5">
              {g.rows.map((m) => {
                const Icon = phase1Icon(m);
                const isBookmarks = m.id === 'bookmarks';
                const active = isBookmarks ? panelOpen : phase1RowActive(m, location);
                return (
                  <li key={m.id}>
                    <button
                      ref={isBookmarks ? bookmarksRowRef : undefined}
                      type="button"
                      data-phase1-row={m.id}
                      onClick={() => open(m)}
                      aria-current={!isBookmarks && active ? 'page' : undefined}
                      aria-expanded={isBookmarks ? panelOpen : undefined}
                      aria-controls={isBookmarks ? 'phase1-bookmarks-panel' : undefined}
                      title={collapsed ? m.label : m.owner && m.owner !== m.label ? m.owner : undefined}
                      aria-label={collapsed ? m.label : undefined}
                      className={cn(
                        'relative flex w-full items-center rounded-lg text-left text-sm transition-colors',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-color)]',
                        collapsed ? 'h-8 w-8 justify-center' : 'gap-3 px-3',
                        !collapsed && (inDrawer ? 'min-h-11 py-2.5' : 'py-2'),
                        active
                          ? 'bg-[var(--sidebar-active)] font-semibold text-[var(--sidebar-active-text)]'
                          : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800',
                      )}
                    >
                      {active && !collapsed ? (
                        <span
                          aria-hidden
                          className="absolute inset-y-1.5 left-0 w-0.5 rounded-r-full bg-[var(--brand-color)]"
                        />
                      ) : null}
                      <Icon className="h-4 w-4 flex-none" />
                      {collapsed ? null : <span className="truncate">{m.label}</span>}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      {showUserMenu && (
        <div className="border-t border-gray-200 p-2 dark:border-gray-700">
          <UserMenu
            expanded={!collapsed}
            onMenuStateChange={onMenuStateChange}
            onImportLegacyChats={onImportLegacyChats}
          />
        </div>
      )}

      {panelOpen ? (
        <BookmarksPanel
          inDrawer={inDrawer}
          onClose={() => {
            setPanelOpen(false);
            bookmarksRowRef.current?.focus();
          }}
          onNavigated={onNavigated}
        />
      ) : null}
    </div>
  );
};

/**
 * The Bookmarks panel. On the desktop it docks beside the rail, where a Level-2
 * column sits; in the phone's More sheet it opens in place under the rows.
 * Unlimited — the list scrolls (prototype: "No limit — this list scrolls").
 */
const BookmarksPanel: React.FC<{
  inDrawer: boolean;
  onClose: () => void;
  onNavigated?: () => void;
}> = ({ inDrawer, onClose, onNavigated }) => {
  const { ids, toggle } = usePhase1Bookmarks();
  const headingRef = useRef<HTMLHeadingElement>(null);

  // The host passes a new onClose on every render (Threads re-renders while a
  // reply streams), so read it through a ref: focus moves to the panel once,
  // when it opens, and never again while it stays open.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    headingRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCloseRef.current();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  return (
    <section
      id="phase1-bookmarks-panel"
      aria-labelledby="phase1-bookmarks-heading"
      data-phase1-bookmarks
      className={cn(
        'flex flex-col bg-white dark:bg-[#161B1C]',
        inDrawer
          ? 'mx-3 mb-2 rounded-xl border border-gray-200 dark:border-gray-700'
          : 'absolute left-full top-0 z-[150] h-full w-60 border-r border-gray-200 shadow-lg dark:border-gray-700',
      )}
    >
      <div className="flex items-center justify-between gap-2 px-3 pb-2 pt-4">
        <h2
          id="phase1-bookmarks-heading"
          ref={headingRef}
          tabIndex={-1}
          className="flex items-center gap-2 text-[14.5px] font-bold text-gray-900 outline-none dark:text-gray-100"
        >
          <Bookmark className="h-4 w-4" aria-hidden /> Bookmarks
        </h2>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close bookmarks"
          className="inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-500 hover:text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-color)] dark:text-gray-400 dark:hover:text-gray-100"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      {ids.length ? (
        <ul className="flex-1 space-y-0.5 overflow-y-auto px-2 pb-3">
          {ids.map((id) => (
            <li key={id} className="group flex items-center">
              <Link
                href={phase1Path(id)}
                onClick={onNavigated}
                className="min-w-0 flex-1 truncate rounded-md px-2.5 py-2 text-[14px] text-gray-700 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-color)] dark:text-gray-300 dark:hover:bg-gray-800"
              >
                {PHASE1_PAGES[id].title}
              </Link>
              <button
                type="button"
                onClick={() => toggle(id)}
                aria-label={`Remove ${PHASE1_PAGES[id].title} from bookmarks`}
                className="inline-flex h-8 w-8 flex-none items-center justify-center rounded-md text-gray-400 hover:text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-color)] dark:hover:text-gray-100"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="px-3 pb-4 text-[13.5px] leading-relaxed text-gray-600 dark:text-gray-400">
          Nothing bookmarked yet. Use the bookmark button at the top of any page to keep it here.
        </p>
      )}
    </section>
  );
};

export default Phase1Rail;
